import { io, type Socket } from "socket.io-client"

import type { Attachment, ChatMessage, Session, Status, VisitorIdentity } from "./types"
import type { ChatOrigin } from "./bridge"

/**
 * O cliente do canal `chatweb`.
 *
 * Duas coisas que este arquivo nao faz, de proposito:
 *
 * 1. Nao conhece o formato de payload da Meta. O que chega no evento `message`
 *    ja foi traduzido na borda (`chatweb-event-translator.ts` no chatbot). Era
 *    o oposto no FloatingChat antigo, que tentava `text.body`,
 *    `interactive.button_reply.title` e mais cinco variantes dentro do
 *    navegador do visitante.
 * 2. Nao escolhe a conversa. Nao existe `phone` em lugar nenhum daqui: a sala e
 *    o claim `conv` do token, decidido no servidor.
 */

const VISITOR_KEY = "tecwb_chat_visitor"
const DEVICE_KEY = "tecwb_chat_device"

export type ClientEvents = {
  onStatus: (status: Status) => void
  onMessage: (message: ChatMessage) => void
  onHandoff: (attendant: { name: string | null; avatarUrl: string | null } | null) => void
  /**
   * A conversa acabou — ou so o ATENDIMENTO acabou.
   *
   * `fluxoSegue` vem do servidor: quando o atendente finaliza e o fluxo tem
   * proximo no, ele continua vivo e e ele que pergunta "Deseja realizar novas
   * operacoes?". Nesse caso a conversa NAO acabou, e tratar como fim fazia a
   * resposta seguinte abrir uma conversa nova — o Sim/Nao morria no vazio.
   */
  onClosed: (fluxoSegue: boolean) => void
  /** O atendente esta escrevendo AGORA. */
  onTyping?: () => void
  /** O atendente leu o que a pessoa mandou. */
  onRead?: (readAt: string) => void
  /**
   * O socket voltou depois de ter ficado fora (queda, ou troca de credencial).
   * O que o servidor emitiu nesse intervalo nao chega de novo por ele.
   */
  onReconectado?: () => void
  onError: (code: string) => void
  /**
   * A credencial esta vencendo (ou venceu) e o widget nao consegue renovar
   * sozinho. Quem resolveu a sessao no servidor tem de resolver de novo.
   */
  onSessionExpiring?: () => void
}

export type SendResult = { ok: true } | { ok: false; error: string }

function readVisitorToken(): string | null {
  try {
    // localStorage DESTE iframe — origem do widget, nao a do site host. O JS da
    // pagina que embute nao alcanca, e a conversa sobrevive ao reload sem
    // depender de cookie cross-site (que o Safari bloquearia).
    return window.localStorage.getItem(VISITOR_KEY)
  } catch {
    return null
  }
}

/**
 * Esta pessoa ja tem conversa guardada neste navegador?
 *
 * Quem tem volta conectado sem esperar clique — reabrir nao cria nada. Quem nao
 * tem so ganha conversa quando abre o painel.
 */
export function temConversaGuardada(): boolean {
  return !!readVisitorToken()
}

function writeVisitorToken(value: string): void {
  try {
    window.localStorage.setItem(VISITOR_KEY, value)
  } catch {
    /* aba anonima ou storage bloqueado: a conversa vale so por esta sessao */
  }
}

/**
 * Um identificador deste NAVEGADOR, para o teto por minuto nao punir quem esta
 * atras do mesmo IP que os colegas.
 *
 * Mora no `localStorage` DESTE iframe — origem nossa, nao a do site que embute.
 * Nao identifica a maquina nem a pessoa: identifica este navegador neste perfil,
 * some quando a pessoa limpa os dados, e nao serve para nada alem do balde.
 *
 * Nao usamos fingerprint de canvas/fontes de proposito: alem de ser
 * rastreamento, ele falha justamente no caso que motivou tudo isto — parque
 * corporativo com maquinas identicas.
 */
/**
 * UUID v4, com plano B.
 *
 * `crypto.randomUUID` so existe em contexto seguro. Num site de cliente servido
 * por `http://` puro ele e `undefined` — e sem plano B a separacao por
 * dispositivo sumia justamente onde o site e mais precario, deixando todo mundo
 * no balde por IP.
 */
function uuid(): string {
  const c: any = globalThis.crypto
  if (typeof c?.randomUUID === "function") return c.randomUUID()

  const bytes = new Uint8Array(16)
  if (typeof c?.getRandomValues === "function") {
    c.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  // Carimba versao 4 e variante, para o formato passar na validacao do servidor.
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function deviceId(): string | null {
  try {
    const guardado = window.localStorage.getItem(DEVICE_KEY)
    if (guardado) return guardado
    const novo = uuid()
    window.localStorage.setItem(DEVICE_KEY, novo)
    return novo
  } catch {
    // Aba anonima ou storage bloqueado: sem id, o servidor cai no teto por IP.
    return null
  }
}

export class ChatwebClient {
  private socket: Socket | null = null
  private session: Session | null = null

  /**
   * O socket esta conectado E o servidor ja sabe QUEM ele e.
   *
   * Nao e a mesma coisa que `socket.connected`. O `connect` do socket.io dispara
   * quando o transporte fecha o aperto de mao; o servidor so grava as claims
   * DEPOIS de um `handleConnection` assincrono, que consulta o banco. Entre um e
   * outro existe uma janela — curta no localhost, longa no Cloud Run com conexao
   * fria — em que a mensagem enviada volta `unauthorized` e se perde.
   *
   * O `ready` do servidor e emitido logo apos as claims serem gravadas. E ele,
   * e nao o `connect`, que autoriza mandar.
   */
  private pronto = false
  private aguardando: Array<() => void> = []
  /**
   * Ja houve um `ready` neste cliente. O primeiro e a abertura, e quem abriu ja
   * busca o historico; os seguintes sao VOLTA — ver `onReconectado`.
   */
  private jaEstevePronto = false

  /**
   * A CONVERSA não tem prazo. A credencial tem.
   *
   * `session_ttl_seconds` (1 h por padrão) vence o token do socket, não a
   * conversa: a linha continua ativa no servidor e reabrir com o mesmo
   * `visitorToken` devolve o MESMO `web-<uuid>`, com o histórico inteiro.
   *
   * Sem esta renovação, porém, o efeito prático era um prazo de validade na
   * conversa: passada uma hora com a janela aberta, o socket era recusado com
   * `token_expired` e a pessoa ficava olhando "indisponível" até recarregar a
   * página. Quem encerra uma conversa é o atendente ou a própria pessoa pela
   * palavra de saída do fluxo — nunca o relógio.
   *
   * Guardamos o token público para renovar sozinhos. Quando ele não existe
   * (o host resolveu a sessão no servidor dele, como faz a loja), pedimos ao
   * host pelo `onSessionExpiring`.
   */
  private publicToken: string | null = null
  private visitorIdentity: VisitorIdentity | null = null
  private renovacao: number | null = null
  private renovando = false

  constructor(
    private readonly apiUrl: string,
    private readonly events: ClientEvents,
  ) {}

  getSession(): Session | null {
    return this.session
  }

  /** Troca o token publico pelo token DESTA conversa e abre o socket. */
  async connect(
    publicToken: string,
    options: {
      userJwt?: string | null
      visitorIdentity?: VisitorIdentity | null
      /** Reabrir a conversa que existir — nunca criar. Devolve `null` se nao ha. */
      reabrirApenas?: boolean
      /** De onde a pessoa veio (pagina, referrer, utm): vira a origem da conversa nova. */
      origin?: ChatOrigin | null
      /** Interesse do carrinho do site (INT-...): o servidor liga a conversa a ele. */
      interestCode?: string | null
    } = {},
  ): Promise<Session | null> {
    this.events.onStatus("connecting")

    const response = await fetch(`${this.apiUrl}/chatweb/session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${publicToken}`,
      },
      body: JSON.stringify({
        visitorToken: readVisitorToken(),
        userJwt: options.userJwt ?? null,
        visitorIdentity: options.visitorIdentity ?? null,
        deviceId: deviceId(),
        reabrirApenas: options.reabrirApenas === true,
        ...(options.origin ? { origin: options.origin } : {}),
        ...(options.interestCode ? { interestCode: options.interestCode } : {}),
      }),
    })

    // 204: nao havia conversa para reabrir, e nao era para criar. Fica so a
    // bolha — a conversa nasce quando a pessoa clicar.
    if (response.status === 204) {
      this.publicToken = publicToken
      this.visitorIdentity = options.visitorIdentity ?? null
      this.events.onStatus("idle")
      return null
    }

    if (!response.ok) {
      this.events.onStatus("unavailable")
      throw new Error(`session_failed_${response.status}`)
    }

    const session = (await response.json()) as Session
    this.session = session
    this.publicToken = publicToken
    this.visitorIdentity = options.visitorIdentity ?? null
    writeVisitorToken(session.conversationId)

    this.openSocket(session.socketToken)
    this.agendarRenovacao(session.expiresAt)
    return session
  }

  /**
   * Abre o socket a partir de uma sessao que o servidor do host ja resolveu.
   * Nao faz `POST /chatweb/session` — a troca ja aconteceu la.
   */
  resume(session: Session): Session {
    this.session = session
    writeVisitorToken(session.conversationId)
    this.openSocket(session.socketToken)
    this.agendarRenovacao(session.expiresAt)
    return session
  }

  /**
   * Marca a renovação para um minuto ANTES do vencimento.
   *
   * Um minuto porque o relógio do visitante não é o do servidor: a folga
   * absorve o desencontro sem deixar a janela morta no intervalo.
   */
  private agendarRenovacao(expiresAt: unknown) {
    this.cancelarRenovacao()
    const vence = Date.parse(String(expiresAt || ""))
    if (!Number.isFinite(vence)) return

    const emQuanto = Math.max(5_000, vence - Date.now() - 60_000)
    this.renovacao = window.setTimeout(() => void this.renovar(), emQuanto)
  }

  private cancelarRenovacao() {
    if (this.renovacao !== null) {
      window.clearTimeout(this.renovacao)
      this.renovacao = null
    }
  }

  /**
   * Troca a credencial mantendo a conversa.
   *
   * `visitorToken` é o próprio `conversationId`: o servidor devolve a MESMA
   * conversa, então nada some da tela — nem histórico, nem quem está atendendo.
   *
   * **Renovar NUNCA reabre.** Trocar credencial é o relógio falando, não a
   * pessoa: sem `reabrirApenas` este pedido era o mesmo que um clique na bolha,
   * e o servidor reativava a conversa que o atendente tinha acabado de
   * finalizar (`reativarConversa`, no `openOrReopen`). O visitante não precisava
   * tocar em nada — bastava o token vencer com a aba aberta, ou a página
   * carregar com um `session_ttl_seconds` curto, e o card voltava para a lista
   * "ATIVAS". É o mesmo defeito que já tinha sido fechado no carregamento da
   * página; faltava fechá-lo aqui.
   *
   * Quem recomeça uma conversa encerrada é quem ESCREVE — o `sendText` pede
   * sessão sem a bandeira, de propósito. Olhar não recomeça (ver
   * `precisaDeConversaAoAbrir`), e o relógio muito menos.
   */
  async renovar(): Promise<boolean> {
    if (this.renovando) return false
    const conversa = this.session?.conversationId
    if (!conversa) return false

    // Sem token público, quem consegue abrir sessão é o host. Ele é avisado e
    // devolve a sessão nova pelo mesmo caminho por que mandou a primeira.
    if (!this.publicToken) {
      this.events.onSessionExpiring?.()
      return false
    }

    this.renovando = true
    try {
      const response = await fetch(`${this.apiUrl}/chatweb/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.publicToken}`,
        },
        body: JSON.stringify({
          visitorToken: conversa,
          visitorIdentity: this.visitorIdentity,
          deviceId: deviceId(),
          reabrirApenas: true,
        }),
      })
      // 204: não há mais o que reanexar — e, com `reabrirApenas`, isso deixou de
      // ser erro para virar resposta legítima. Quem resolveu a sessão que
      // resolva de novo; o próximo clique (ou a próxima mensagem) abre outra.
      if (response.status === 204 || !response.ok) {
        // Nada de `unavailable` aqui: a conversa continua viva do lado de lá, e
        // a próxima tentativa (ou o host) ainda pode reabri-la.
        this.events.onSessionExpiring?.()
        return false
      }

      const session = (await response.json()) as Session
      this.session = session
      writeVisitorToken(session.conversationId)
      this.openSocket(session.socketToken)
      this.agendarRenovacao(session.expiresAt)
      return true
    } catch {
      this.events.onSessionExpiring?.()
      return false
    } finally {
      this.renovando = false
    }
  }

  private openSocket(token: string) {
    this.socket?.disconnect()
    this.pronto = false

    const socket = io(`${this.apiUrl}/chatweb`, {
      transports: ["websocket", "polling"],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 8,
    })
    this.socket = socket

    socket.on("connect", () => this.events.onStatus("connected"))
    socket.on("ready", () => {
      this.pronto = true
      this.aguardando.splice(0).forEach((solta) => solta())
      this.events.onStatus("connected")
      if (this.jaEstevePronto) this.events.onReconectado?.()
      this.jaEstevePronto = true
    })
    socket.on("disconnect", () => {
      // A reconexao refaz o `handleConnection`, entao as claims valem por
      // conexao — nao por sessao.
      this.pronto = false
      this.events.onStatus("reconnecting")
    })
    socket.on("connect_error", () => this.events.onStatus("reconnecting"))

    // O socket.io desiste depois de `reconnectionAttempts`. Sem isto, a janela
    // ficava "reconectando..." para sempre — e a pessoa so descobria
    // recarregando a pagina. Desistir do SOCKET nao e desistir da CONVERSA: ela
    // continua viva no servidor, entao pedimos credencial nova e comecamos de
    // novo.
    socket.io.on("reconnect_failed", () => {
      void this.renovar().then((ok) => {
        if (!ok) this.events.onStatus("unavailable")
      })
    })

    socket.on("message", (message: ChatMessage) => this.events.onMessage(message))
    // O "digitando" de VERDADE, que o servidor emite quando o atendente esta
    // escrevendo. Ate agora ele era ignorado, e a tela fabricava o proprio.
    socket.on("typing", () => this.events.onTyping?.())
    socket.on("read", (payload: any) => {
      const readAt = typeof payload?.readAt === "string" ? payload.readAt : ""
      if (readAt) this.events.onRead?.(readAt)
    })
    socket.on("handoff", (payload: any) => this.events.onHandoff(payload?.attendant ?? null))
    socket.on("closed", (payload: any) =>
      this.events.onClosed(payload?.flowContinues === true),
    )
    socket.on("error", (payload: any) => {
      const codigo = String(payload?.message || "error")
      // Credencial vencida NAO e fim de conversa: renova e reconecta na MESMA
      // sala. So se a renovacao falhar e que o erro sobe para a tela.
      if (codigo === "unauthorized") {
        void this.renovar().then((ok) => {
          if (!ok) this.events.onError(codigo)
        })
        return
      }
      this.events.onError(codigo)
    })
  }

  /**
   * Historico paginado, ja no mesmo formato do evento `message`.
   *
   * O cursor e OPACO: guarde e devolva, nao interprete. Hoje ele e um token que
   * carrega `(sent_at, id)` da mensagem mais antiga da pagina; em conversa
   * antiga (que ainda vive no array) ainda e um indice. Era tipado como
   * `number` justamente por causa do indice — o uso ja tratava como opaco.
   */
  async history(cursor?: string | null): Promise<{
    messages: ChatMessage[]
    nextCursor: string | null
  }> {
    if (!this.session) return { messages: [], nextCursor: null }

    const params = new URLSearchParams({ limit: "30" })
    if (cursor !== null && cursor !== undefined) params.set("cursor", String(cursor))

    const response = await fetch(
      `${this.apiUrl}/chatweb/messages?${params.toString()}`,
      { headers: { Authorization: `Bearer ${this.session.socketToken}` } },
    )
    if (!response.ok) return { messages: [], nextCursor: null }
    return response.json()
  }

  /**
   * Espera o `ready` do servidor, com teto.
   *
   * O teto existe para o caso em que o `ready` nunca vem — token recusado, por
   * exemplo. Ai a mensagem falha com o erro do servidor, como falharia antes,
   * em vez de a promessa ficar pendurada.
   */
  private aguardarPronto(msTeto = 5000): Promise<void> {
    if (this.pronto) return Promise.resolve()
    return new Promise((resolve) => {
      let resolvido = false
      const solta = () => {
        if (resolvido) return
        resolvido = true
        window.clearTimeout(timer)
        resolve()
      }
      const timer = window.setTimeout(solta, msTeto)
      this.aguardando.push(solta)
    })
  }

  async send(
    text: string,
    attachment?: Attachment | null,
    /** Botao clicado. Vai junto para o fluxo casar pelo ID, nao pelo rotulo. */
    option?: { id: string; title: string } | null,
  ): Promise<SendResult> {
    if (!this.socket?.connected) {
      return { ok: false, error: "sem_conexao" }
    }
    // Conectado nao basta: ver `pronto`. Sem esta espera, quem escreve rapido
    // (ou o envio de anexo, que vem logo depois do upload) leva `unauthorized`
    // e perde a mensagem.
    await this.aguardarPronto()
    // Texto e o anexo JA SUBIDO — nunca um identificador de conversa. A sala e
    // a do token; mandar destino daqui era o que deixava escrever na conversa
    // de outra pessoa.
    return new Promise((resolve) => {
      let settled = false
      const timer = window.setTimeout(() => {
        if (settled) return
        settled = true
        resolve({ ok: false, error: "timeout" })
      }, 10000)

      this.socket?.emit(
        "message",
        { text, attachment: attachment ?? undefined, option: option ?? undefined },
        (ack: any) => {
          if (settled) return
          settled = true
          window.clearTimeout(timer)
          if (ack?.ok === true) {
            resolve({ ok: true })
            return
          }
          resolve({ ok: false, error: String(ack?.error || "send_failed") })
        },
      )
    })
  }

  /**
   * Sobe o arquivo ANTES de mandar a mensagem.
   *
   * Separado de proposito: assim da para mostrar progresso, e um upload que
   * falha nao leva junto o texto que a pessoa escreveu.
   */
  async upload(file: File): Promise<Attachment> {
    if (!this.session) throw new Error("sem_sessao")

    const form = new FormData()
    form.append("file", file)

    const response = await fetch(`${this.apiUrl}/chatweb/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${this.session.socketToken}` },
      body: form,
    })

    if (!response.ok) {
      // O codigo do servidor sobe inteiro: quem chama decide o que dizer para a
      // pessoa (arquivo grande, tipo recusado, teto por minuto).
      const detalhe = await response.text().catch(() => "")
      throw new Error(`upload_${response.status}:${detalhe.slice(0, 120)}`)
    }

    return (await response.json()) as Attachment
  }

  close(): void {
    this.socket?.emit("close")
  }

  /**
   * "Já li o que vocês mandaram."
   *
   * Só faz sentido com a conversa na frente da pessoa — quem decide isso é o
   * App, que sabe se a janela está aberta e a aba visível. Aqui é só o aviso.
   *
   * Sem retorno e sem retentativa de propósito: perder um aviso destes atrasa um
   * visto, e o próximo (mensagem nova, voltar para a aba) corrige.
   */
  marcarLido(): void {
    this.socket?.emit("read")
  }

  destroy(): void {
    this.cancelarRenovacao()
    this.socket?.disconnect()
    this.socket = null
  }
}
